import type { SerializedConstructor } from '@langchain/core/load/serializable';

import { dequal } from 'dequal';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';

import { hasNestedObjects } from '@/components/PrettyJSONEditor/utils.ts';
import { simplifyToolChoice } from '@/utils/ai-tools.tsx';

import {
  BUILTIN_TOOL_TYPES_THAT_DISABLE_STREAMING,
  DEFAULT_CHAT_MODEL_CONSTRUCTOR,
} from '../../Playground.constants.tsx';
import {
  ExampleSchemaOrDraftWithEdited,
  PlaygroundContextType,
  PlaygroundInputSourceType,
  PlaygroundPromptType,
} from '../../PlaygroundContext';
import { getOpenAIModelCapabilities } from '../../components/manifest/serialized/chat_models/ManifestChatOpenAI.utils.ts';
import {
  createExperimentName,
  createPlaygroundManifest,
  getDefaultModelManifest,
  getDefaultPromptManifest,
  getInputVariablesFromPrompt,
  getModelFromManifest,
  getModelNameFromRunnableBinding,
} from '../../utils/Playground.utils.tsx';
import { ManifestOptions } from '../../utils/types.ts';
import { PlaygroundStoreContext } from '../PlaygroundStoreContext.tsx';
import {
  PlaygroundActions,
  PlaygroundInputValueType,
  PlaygroundMainProps,
} from '../types.tsx';
import { createNewExample } from '../utils.ts';

const DEFAULT_INPUT_TYPE = PlaygroundInputSourceType.MANUAL;
const DEFAULT_DATASET_SPLITS = [];

export const createPlaygroundStore = () =>
  create<PlaygroundContextType & PlaygroundActions>((set) => ({
    defaultModelManifest: undefined,
    prompts: [],
    input: {},
    datasetId: undefined,
    datasetSplits: DEFAULT_DATASET_SPLITS,
    excludedEvaluatorIds: new Set<string>(),
    examples: [],
    exampleCount: 0,
    inputSourceType: DEFAULT_INPUT_TYPE,
    repetitions: 1,
    loadingState: 'done',
    evaluatorFeedbackKeys: [],
    expectedFeedbackKeys: [],
    abortController: new AbortController(),
    originalRunId: null,
    isStreaming: true,
    isCreatingNewDataset: false,
    isAlignEvaluator: false,
    expectedNumberOfRuns: 0,
    enableMarkdown: true, // Default to markdown enabled
    setIsAlignEvaluator: (isAlignEvaluator: boolean) =>
      set({ isAlignEvaluator }),
    setExpectedNumberOfRuns: (expectedNumberOfRuns: number) =>
      set({ expectedNumberOfRuns }),

    initializeState: (props: PlaygroundMainProps) =>
      set(() => {
        const initialPlaygroundManifest = createPlaygroundManifest(
          props.promptManifest,
          props.modelManifest
        );
        let partialVariables = props.promptManifest?.kwargs?.partial_variables;
        if (
          typeof partialVariables !== 'object' ||
          partialVariables == null ||
          Array.isArray(partialVariables)
        ) {
          partialVariables = {};
        }

        const isStreaming = !props.options?.tools?.some(
          (tool) =>
            'type' in tool &&
            BUILTIN_TOOL_TYPES_THAT_DISABLE_STREAMING.includes(tool.type)
        );

        return {
          defaultModelManifest: props.modelManifest,
          prompts: [
            {
              columnId: uuidv4(),
              manifest: initialPlaygroundManifest,
              options: {
                ...props.options,
                // https://langchain.slack.com/archives/C05RL6DPH4M/p1736973510658829?thread_ts=1736963845.125499&cid=C05RL6DPH4M
                // all providers that support tool_choice should support this simplified format (which is just the tool name as a string)
                tool_choice: simplifyToolChoice(props.options?.tool_choice),
              },
              hubPrompt: props.repo
                ? {
                    prompt: props.repo,
                    edited: false,
                    commitHash: props.commitHash,
                  }
                : undefined,
              runs: props.run ? [props.run] : [],
              customExperimentName: createExperimentName({
                modelManifest: props.modelManifest,
              }),
            },
          ],
          input:
            props.run?.run_type === 'llm'
              ? {}
              : {
                  ...partialVariables,
                  ...props.run?.inputs,
                },
          datasetId:
            props.inputSourceType === PlaygroundInputSourceType.MANUAL
              ? undefined
              : props.run
              ? undefined
              : props.datasetId,
          datasetSplits: DEFAULT_DATASET_SPLITS,
          excludedEvaluatorIds: new Set<string>(),
          inputSourceType:
            props.inputSourceType ?? props.run
              ? PlaygroundInputSourceType.MANUAL
              : props.datasetId
              ? PlaygroundInputSourceType.DATASET
              : DEFAULT_INPUT_TYPE,
          originalRunId: props.run?.id,
          isCreatingNewDataset: false,
          isStreaming,
        };
      }),
    resetState: () =>
      set((state) => {
        const modelManifest =
          state.defaultModelManifest ??
          getDefaultModelManifest(DEFAULT_CHAT_MODEL_CONSTRUCTOR);
        const defaultPlaygroundManifest = createPlaygroundManifest(
          getDefaultPromptManifest({ modelManifest }),
          modelManifest
        );

        return {
          prompts: [
            {
              columnId: uuidv4(),
              manifest: defaultPlaygroundManifest,
              options: {},
              hubPrompt: undefined,
              runs: [],
            },
          ],
          input: {},
          datasetId: undefined,
          datasetSplits: DEFAULT_DATASET_SPLITS,
          excludedEvaluatorIds: new Set<string>(),
          inputSourceType: DEFAULT_INPUT_TYPE,
          originalRunId: null,
        };
      }),
    resetPrompt: (index: number, modelManifest?: SerializedConstructor) =>
      set((state) => {
        const modelManifestToUse =
          modelManifest ??
          state.defaultModelManifest ??
          getDefaultModelManifest(DEFAULT_CHAT_MODEL_CONSTRUCTOR);
        const defaultPlaygroundManifest = createPlaygroundManifest(
          getDefaultPromptManifest({ modelManifest: modelManifestToUse }),
          modelManifestToUse
        );
        const prompts = [...state.prompts];
        prompts[index] = {
          columnId: uuidv4(),
          manifest: defaultPlaygroundManifest,
          options: {},
          hubPrompt: undefined,
          runs: [],
          customExperimentName: createExperimentName({
            modelManifest: modelManifestToUse,
          }),
        };
        return { prompts };
      }),
    resetAbortController: () =>
      set(() => {
        return { abortController: new AbortController() };
      }),
    setManifests: (manifests) =>
      set((state) => {
        const prompts = [...state.prompts];
        const newManifests =
          typeof manifests === 'function'
            ? manifests(prompts.map((p) => p.manifest))
            : manifests;
        for (let i = 0; i < newManifests.length; i++) {
          prompts[i].manifest = newManifests[i];
        }
        return { prompts };
      }),
    setManifest: (
      index: number,
      manifest:
        | SerializedConstructor
        | ((prev: SerializedConstructor) => SerializedConstructor)
    ) =>
      set((state) => {
        const prompts = [...state.prompts];
        const newManifest =
          typeof manifest === 'function'
            ? manifest(state.prompts[index].manifest)
            : manifest;

        prompts[index] = {
          ...prompts[index],
          manifest: newManifest,
          customExperimentName: createExperimentName({
            repoHandle: state.prompts[index].hubPrompt?.prompt.repo_handle,
            modelManifest: getModelFromManifest(newManifest) ?? undefined,
          }),
        };
        return { prompts };
      }),
    addPrompt: () =>
      set((state) => ({
        prompts: [
          ...state.prompts,
          {
            ...state.prompts[0],
            columnId: uuidv4(),
            runs: [],
            sessionId: undefined,
            hubPrompt: state.prompts[0].hubPrompt,
            customExperimentName: createExperimentName({
              repoHandle: state.prompts[0].hubPrompt?.prompt.repo_handle,
              modelManifest:
                getModelFromManifest(state.prompts[0].manifest) ?? undefined,
            }),
          },
        ],
      })),
    removePrompt: (columnId: string) =>
      set((state) => ({
        prompts: state.prompts.filter((p) => p.columnId !== columnId),
      })),
    setLoadingState: (loading: 'loading' | 'done') =>
      set({ loadingState: loading }),
    setInput: (input: Record<string, PlaygroundInputValueType>) =>
      set({ input }),
    setSingleInput: (name: string, value: PlaygroundInputValueType) =>
      set((state) => ({ input: { ...state.input, [name]: value } })),
    setInputSourceTypeAndUpdateUrl: (
      inputSourceType: PlaygroundInputSourceType
    ) => {
      set({ inputSourceType });
      if (inputSourceType === PlaygroundInputSourceType.MANUAL) {
        set({ datasetId: undefined });
        setDatasetIdInUrl();
      }
    },
    setDatasetIdAndUpdateUrl: (datasetId?: string) => {
      set({ datasetId });
      set({ isCreatingNewDataset: false });
      if (!datasetId) {
        set({
          inputSourceType: PlaygroundInputSourceType.MANUAL,
          datasetSplits: DEFAULT_DATASET_SPLITS,
          excludedEvaluatorIds: new Set<string>(),
          examples: [],
          exampleCount: 0,
        });
        setDatasetIdInUrl();
      } else {
        set({ inputSourceType: PlaygroundInputSourceType.DATASET });
        set((state) =>
          state.datasetId === datasetId
            ? state
            : {
                ...state,
                datasetSplits: DEFAULT_DATASET_SPLITS,
                excludedEvaluatorIds: new Set<string>(),
              }
        );
        setDatasetIdInUrl(datasetId);
      }
    },
    setDatasetSplits: (splits: string[]) => set({ datasetSplits: splits }),
    setExcludedEvaluatorIds: (excludedIds: Set<string>) =>
      set({ excludedEvaluatorIds: excludedIds }),
    setExamples: (examples: ExampleSchemaOrDraftWithEdited[]) =>
      set({ examples: examples.map((e) => ({ ...e, edited: false })) }),
    removeReferenceOutputFromExamples: () =>
      set((state) => {
        if (!state.isCreatingNewDataset) {
          return state;
        }
        return {
          examples: state.examples.map((e) => ({
            ...e,
            outputs: {},
            edited: true,
          })),
        };
      }),
    setExample: (
      exampleId: string,
      example: (
        prevExample: ExampleSchemaOrDraftWithEdited
      ) => ExampleSchemaOrDraftWithEdited
    ) => {
      set((state) => ({
        examples: state.examples.map((e) =>
          e.id === exampleId ? example(e) : e
        ),
      }));
    },
    clearRuns: (preserveSessionId?: boolean) =>
      set((state) => ({
        prompts: state.prompts.map((p) => ({
          ...p,
          runs: [],
          ...(!preserveSessionId ? { sessionId: undefined } : {}),
        })),
      })),
    setPrompt: (
      index: number,
      prompt:
        | PlaygroundPromptType
        | ((prev: PlaygroundPromptType) => PlaygroundPromptType)
    ) => {
      set((state) => {
        const newPrompts = { prompts: [...state.prompts] };

        const newPrompt =
          typeof prompt === 'function' ? prompt(state.prompts[index]) : prompt;

        newPrompts.prompts[index] = newPrompt;
        return newPrompts;
      });
    },
    setEvaluatorFeedbackKeys: (keys: string[]) =>
      set({ evaluatorFeedbackKeys: keys }),
    setExpectedFeedbackKeys: (keys: string[]) =>
      set({ expectedFeedbackKeys: keys }),
    setRepetitions: (num: number) => set({ repetitions: num }),
    setEdited: (index: number, edited: boolean) =>
      set((state) => {
        const prompts = [...state.prompts];
        if (prompts[index].hubPrompt) {
          prompts[index].hubPrompt!.edited = edited;
        }
        return { prompts };
      }),
    setOptions: (index: number, options: ManifestOptions) =>
      set((state) => {
        const prompts = [...state.prompts];
        prompts[index].options = options;
        return { prompts };
      }),
    setCompareToOptimizedManifest: (optimizedManifest: SerializedConstructor) =>
      set((state) => ({
        prompts: [
          state.prompts[0],
          {
            ...state.prompts[0],
            manifest: optimizedManifest,
            columnId: uuidv4(),
            runs: [],
            sessionId: undefined,
            hubPrompt: undefined,
          },
        ],
      })),
    setIsStreaming: (isStreaming: boolean) => set({ isStreaming }),
    setIsCreatingNewDataset: (isCreatingNewDataset: boolean) =>
      set({ isCreatingNewDataset }),
    onCreateNewDataset: (useInputValues: boolean = false) => {
      set((state) => {
        // Only use the input if it matches our criteria for creating datasets inline:
        // 1. useInputValues is true
        // 2. the input is not nested
        // 3. all values are strings
        const values = Object.values(state.input);
        const inputValues =
          useInputValues &&
          !hasNestedObjects(state.input) &&
          values.length &&
          values.every((v) => typeof v === 'string')
            ? (state.input as Record<string, string>)
            : undefined;

        const inputVariables = getInputVariablesFromPrompt(state.prompts[0]);
        const outputVariables = ['output'];
        return {
          datasetSplits: DEFAULT_DATASET_SPLITS,
          inputSourceType: PlaygroundInputSourceType.DATASET,
          datasetId: undefined,
          isCreatingNewDataset: true,
          excludedEvaluatorIds: new Set<string>(),
          examples: [
            createNewExample({ inputVariables, outputVariables, inputValues }),
          ],
          exampleCount: 1,
        };
      });
      setDatasetIdInUrl();
    },
    addNewExample: () => {
      set((state) => {
        const inputVariables = getInputVariablesFromPrompt(state.prompts[0]);
        const outputVariables = state.examples[0]
          ? Object.keys(state.examples[0].outputs)
          : ['output'];
        return {
          examples: [
            createNewExample({ inputVariables, outputVariables }),
            ...state.examples,
          ],
          exampleCount: state.exampleCount + 1,
        };
      });
    },
    decreaseExampleCount: () =>
      set((state) => ({ exampleCount: state.exampleCount - 1 })),
    increaseExampleCount: () =>
      set((state) => ({ exampleCount: state.exampleCount + 1 })),
    setExampleCount: (exampleCount: number) => set({ exampleCount }),
    markExamplesAsSaved: () => {
      set((state) => {
        const examples = state.examples.map((e) => ({
          ...e,
          edited: false,
        }));
        return { examples };
      });
    },
    saveDataset: () => {
      set((state) => {
        const examples = state.examples.map((e) => ({
          ...e,
          modified_at: new Date().toISOString(),
        }));
        return { examples };
      });
    },
    removeExampleFromDataset: (exampleId: string) => {
      set((state) => ({
        examples: state.examples.filter((e) => e.id !== exampleId),
        exampleCount: state.exampleCount - 1,
      }));
    },
    setEnableMarkdown: (enableMarkdown: boolean) => set({ enableMarkdown }),
  }));

const setDatasetIdInUrl = (datasetId?: string) => {
  const url = new URL(window.location.href);
  if (datasetId) {
    url.searchParams.set('datasetId', datasetId);
  } else {
    url.searchParams.delete('datasetId');
  }
  window.history.replaceState({}, '', url);
};

export const useStandardPlaygroundStore = createPlaygroundStore();
export const useAlignmentPlaygroundStore = createPlaygroundStore();

export type PlaygroundStoreType = 'standard' | 'alignment';

// Function overloads for usePlaygroundStore
export function usePlaygroundStore<T>(
  selector: (state: PlaygroundContextType & PlaygroundActions) => T
): T;
export function usePlaygroundStore(): PlaygroundContextType & PlaygroundActions;

// Implementation that determines which store to use based on context
export function usePlaygroundStore<T>(
  selector?: (state: PlaygroundContextType & PlaygroundActions) => T
) {
  const storeType = useContext(PlaygroundStoreContext);

  // Always call both hooks to satisfy React hooks rules
  const standardStore = useStandardPlaygroundStore();
  const alignmentStore = useAlignmentPlaygroundStore();

  const selectedStore =
    storeType === 'alignment' ? alignmentStore : standardStore;

  // Apply selector if provided, otherwise return the full store
  return selector ? selector(selectedStore) : selectedStore;
}

export const useManifests = () =>
  usePlaygroundStore(useDeep((state) => state.prompts.map((p) => p.manifest)));

export const useDisabledStreamingText = ({
  prompts,
  manifests,
}: {
  prompts: PlaygroundPromptType[] | undefined;
  manifests: SerializedConstructor[] | undefined;
}) => {
  const promptCount = prompts?.length ?? 0;
  const hasStructuredPrompt = prompts?.some(
    (p) => p.manifest.kwargs?.first?.id?.at(-1) === 'StructuredPrompt'
  );
  const hasModelThatDoesNotSupportStreaming = useMemo(() => {
    return manifests?.some((manifest) => {
      const model = getModelFromManifest(manifest);
      if (model) {
        const modelName = getModelNameFromRunnableBinding(model);
        const { isStreamingSupported } = getOpenAIModelCapabilities(modelName);
        return !isStreamingSupported;
      }
      return false;
    });
  }, [manifests]);

  // has a tool that disables streaming
  const hasToolThatDisablesStreaming = useMemo(() => {
    return prompts?.some((p) =>
      p.options?.tools?.some(
        (tool) =>
          'type' in tool &&
          BUILTIN_TOOL_TYPES_THAT_DISABLE_STREAMING.includes(tool.type)
      )
    );
  }, [prompts]);

  if (promptCount > 3) {
    return 'Streaming allowed with 3 or fewer prompts';
  }
  if (hasModelThatDoesNotSupportStreaming) {
    return 'This model does not support streaming';
  }
  if (hasToolThatDisablesStreaming) {
    return (
      'Using a builtin tool that disables streaming: ' +
      BUILTIN_TOOL_TYPES_THAT_DISABLE_STREAMING.join(', ')
    );
  }

  return hasStructuredPrompt
    ? 'Streaming disabled with structured output'
    : undefined;
};

export const useInput = () =>
  usePlaygroundStore(useDeep((state) => state.input));

// compare two objects, treating missing values as equal to undefined
export function deepEqualIgnoreUndefined(a: any, b: any): boolean {
  return dequal(normalizeUndefined(a), normalizeUndefined(b));
}

export function normalizeUndefined(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(normalizeUndefined);
  }
  if (obj instanceof Date) {
    return obj;
  }

  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      result[key] = normalizeUndefined(value);
    }
  }
  return result;
}

export function useDeep<T, U>(selector: (state: T) => U) {
  const ref = useRef<U>();
  return useCallback(
    (state: T) => {
      const selected = selector(state);
      if (ref.current === undefined || !dequal(ref.current, selected)) {
        ref.current = selected;
      }
      return ref.current;
    },
    [selector]
  );
}

export function isDatasetExamplesEdited(
  examples?: ExampleSchemaOrDraftWithEdited[]
) {
  return examples?.some((e) => e.edited);
}
